import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail, MapPin, Phone } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Use local transparent logo (white background removed)
const LOGO_URL = "/images/logo-transparent.png";

const quickLinks = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' },
  { name: 'Menu', path: '/menu' },
  { name: 'Contact', path: '/contact' },
];

const exploreLinks = [
  { name: 'Gallery', path: '/gallery' },
  { name: 'Location', path: '/location' },
  { name: 'Franchise Enquiry', path: '/franchise' },
  { name: 'Privacy Policy', path: '/privacy' },
  { name: 'Data Deletion', path: '/data-deletion' },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      setSubscribed(true);
      setEmail('');
    } catch (error) {
      console.error('Newsletter signup error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-black text-white" data-testid="main-footer">
      {/* Newsletter Section */}
      <div className="border-b border-neutral-800">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] text-neutral-400 uppercase mb-3">Stay Connected</p>
              <h3 className="font-serif text-3xl md:text-4xl font-medium italic">
                Sign up to our newsletter
              </h3>
              <p className="text-neutral-400 mt-4 text-sm">
                Receive special moments before anyone else.
              </p>
            </div>
            <div>
              {subscribed ? (
                <p className="text-lg" data-testid="newsletter-success">Thank you for subscribing!</p>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    data-testid="newsletter-email-input"
                    className="flex-1 bg-transparent border-b border-neutral-600 px-0 py-3 text-white focus:border-white focus:outline-none placeholder:text-neutral-500"
                    required
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="newsletter-submit-btn"
                    className="bg-white text-black px-8 py-3 text-xs tracking-widest uppercase font-bold hover:bg-neutral-200 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? 'Signing up...' : 'Sign Up'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img
              src={LOGO_URL}
              alt="Honey I'm Home Coffee"
              className="h-12 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-neutral-400 text-sm leading-relaxed mb-6">
              Organic artisan coffee experience and Makan.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.instagram.com/honeyimhome.coffee/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="text-neutral-400 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="#"
                data-testid="footer-facebook"
                className="text-neutral-400 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href="mailto:Enquiries@honeyimhomecoffee.com"
                data-testid="footer-email"
                className="text-neutral-400 hover:text-white transition-colors"
                aria-label="Email"
              >
                <Mail size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs tracking-[0.2em] text-neutral-400 uppercase mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-neutral-300 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-xs tracking-[0.2em] text-neutral-400 uppercase mb-6">Explore</h4>
            <ul className="space-y-3">
              {exploreLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-neutral-300 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xs tracking-[0.2em] text-neutral-400 uppercase mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-neutral-400 mt-1 flex-shrink-0" />
                <span className="text-neutral-300 text-sm">
                  451 High Road<br />
                  Woodford Green, London<br />
                  IG8 0XE
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone size={18} className="text-neutral-400 flex-shrink-0" />
                <a href="tel:+447739083131" className="text-neutral-300 hover:text-white transition-colors text-sm">
                  +44 7739 083131
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail size={18} className="text-neutral-400 flex-shrink-0" />
                <a href="mailto:Enquiries@honeyimhomecoffee.com" className="text-neutral-300 hover:text-white transition-colors text-sm">
                  Enquiries@honeyimhomecoffee.com
                </a>
              </li>
            </ul>
            
            {/* Opening Hours */}
            <h4 className="text-xs tracking-[0.2em] text-neutral-400 uppercase mt-8 mb-4">Opening Hours</h4>
            <ul className="space-y-1 text-neutral-300 text-sm">
              <li className="flex justify-between">
                <span>Monday</span>
                <span className="text-neutral-500">Closed</span>
              </li>
              <li className="flex justify-between">
                <span>Tue - Fri</span>
                <span>8:00 AM - 5:00 PM</span>
              </li>
              <li className="flex justify-between">
                <span>Sat - Sun</span>
                <span>8:30 AM - 5:00 PM</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Sustainability Partner & Meta Verified */}
        <div className="mt-12 pt-8 border-t border-neutral-800">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            {/* Meta Verified Badge */}
            <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
              <svg viewBox="0 0 24 24" width="20" height="20" className="text-blue-500 fill-current">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span className="text-white text-xs font-medium">Meta Verified Business</span>
            </div>
            
            {/* Good Cup Partner */}
            <a 
              href="https://thegoodcup.world" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-[#1a472a] hover:bg-[#245a35] transition-colors px-5 py-3 rounded-full"
              data-testid="goodcup-footer-link"
            >
              <div className="flex items-center gap-2">
                <span className="text-xl">🌱</span>
                <div className="text-left">
                  <p className="text-white font-bold text-sm tracking-wide">THE GOOD CUP</p>
                  <p className="text-white/60 text-xs">100% Recyclable Cups</p>
                </div>
              </div>
              <div className="bg-green-400 text-green-900 px-2 py-0.5 rounded text-xs font-bold">
                ECO
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-neutral-800">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-neutral-500 text-xs">
            <Link to="/admin" className="hover:text-neutral-400 transition-colors cursor-default">
              © {new Date().getFullYear()} Honey I'm Home Coffee. All rights reserved.
            </Link>
            <p className="mt-2 md:mt-0">Designed with love for coffee</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
